import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import type { ModelData } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface ChartsTabProps {
  data: ModelData;
  modelType?: string;
}

export function ChartsTab({ data }: ChartsTabProps) {
  const years = data?.years || [];
  const revenue = data?.revenue || [];
  const pnl = data?.pnl || [];
  const customers = data?.customers || [];
  const cashFlow = data?.cashFlow || [];

  // Handle case where data is not yet loaded
  if (!years.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">📈</div>
          <p className="text-slate-500">No chart data available</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = years.map((year, i) => ({
    name: `Year ${year}`,
    year,
    revenue: revenue[i]?.total || 0,
    subscriptionRevenue: revenue[i]?.subscription || 0,
    usageRevenue: revenue[i]?.usage || 0,
    expansionRevenue: revenue[i]?.expansion || 0,
    grossProfit: pnl[i]?.grossProfit || 0,
    ebitda: pnl[i]?.ebitda || 0,
    netIncome: pnl[i]?.netIncome || 0,
    customers: customers[i] || 0,
    cashFlow: Array.isArray(cashFlow) && typeof cashFlow[i] === 'number' ? cashFlow[i] : 0,
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ color: string; name: string; value: number }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 text-sm">
          <p className="font-semibold text-slate-800 mb-2">{label}</p>
          {payload.map((entry, index: number) => (
            <p key={index} className="flex justify-between gap-4" style={{ color: entry.color }}>
              <span>{entry.name}:</span>
              <span className="font-semibold">{formatCurrency(entry.value)}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Revenue Chart */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">📈 Revenue Growth</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" />
            <YAxis
              stroke="#64748b"
              tickFormatter={(value: number) => `$${(value / 1000).toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {revenue[0]?.subscription !== undefined && (
              <Bar dataKey="subscriptionRevenue" name="Subscription" fill="#3b82f6" stackId="a" />
            )}
            {revenue[0]?.usage !== undefined && (
              <Bar dataKey="usageRevenue" name="Usage" fill="#8b5cf6" stackId="a" />
            )}
            {revenue[0]?.expansion !== undefined && (
              <Bar dataKey="expansionRevenue" name="Expansion" fill="#06b6d4" stackId="a" />
            )}
            {!revenue[0]?.subscription && (
              <Bar dataKey="revenue" name="Total Revenue" fill="#3b82f6" />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Profitability Chart */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">💰 Profitability Metrics</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" />
            <YAxis
              stroke="#64748b"
              tickFormatter={(value: number) => `$${(value / 1000).toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="grossProfit"
              name="Gross Profit"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: '#22c55e', strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="ebitda"
              name="EBITDA"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="netIncome"
              name="Net Income"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ fill: '#ef4444', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Customers Chart (if applicable) */}
      {customers.length > 0 && customers.some((c: number) => c > 0) && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">👥 Customer Growth</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                formatter={(value) => [Number(value).toLocaleString(), 'Customers']}
              />
              <Bar dataKey="customers" name="Customers" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cash Flow Chart */}
      {cashFlow.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">💵 Annual Cash Flow</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis
                stroke="#64748b"
                tickFormatter={(value: number) => `$${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="cashFlow"
                name="Net Cash Flow"
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.cashFlow >= 0 ? '#22c55e' : '#ef4444'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
